import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { shallow } from "zustand/shallow";

import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import { type CreateNotification } from "@hsl/fc-subscription/types";

import useTranslation, { Translate } from "../../../hooks/useTranslation";
import SvgCircleBellIcon from "../../icons/CircleBell";
import SvgCircleBellWithBubbleIcon from "../../icons/CircleBellWithBubble";
import SvgCircleShareIcon from "../../icons/CircleShare";
import SvgCircleStarIcon from "../../icons/CircleStar";
import NotificationsBooth from "../../NotificationsBooth";
import { type Notice } from "../../NotificationsBooth/types";
import { txStrReplacer as replacer } from "../lib";

type TX = ReturnType<typeof useTranslation>;

const SubscriptionNotification = () => {
    const tx = useTranslation();
    const {
        notificationQueue,
        setNotificationQueue,
        createNotificationContext,
    } = useFCSubscriptionStore(
        ({
            notificationQueue,
            setNotificationQueue,
            createNotificationContext,
        }) => ({
            notificationQueue,
            setNotificationQueue,
            createNotificationContext,
        }),
        shallow,
    );
    if (notificationQueue.length === 0) {
        return <></>;
    }
    const notifications = notificationQueue
        .map(createNotificationContext)
        .map(toNotification.bind(null, tx));
    return (
        <NotificationsBooth
            notices={notifications}
            duration={5000}
            onComplete={() => setNotificationQueue()}
        />
    );
};

export default SubscriptionNotification;

const Title = ({
    title,
    className,
}: {
    title: ReactNode;
    className?: string;
}) => {
    return (
        <span className={cx("text-base font-medium", className)}>{title}</span>
    );
};

function toNotification(tx: TX, item: CreateNotification | null): Notice {
    if (!item) {
        return {
            id: `temp-${Date.now()}`,
            title: "",
        };
    }
    const { id } = item;
    const title = <Title title={getNotificationTitle(item)} />;
    const message = getNotificationMessage(item, tx);
    const icon = getNotificationIcon(item);
    return {
        id,
        title,
        message,
        icon,
    };
}

function getNotificationIcon(item: CreateNotification) {
    switch (item.type) {
        case "WMF":
        case "WBMF":
        case "WMF_SHARE":
        case "WMF_SHARE_LINK":
        case "WB":
        case "WB_SHARE":
        case "WB_SHARE_LINK":
        case "NFS":
        case "SHARE_LINK":
            return (
                <SvgCircleBellIcon
                    width={32}
                    height={32}
                    className="text-core-blue"
                />
            );
        case "WB_WATCH":
        case "WMF_WATCH":
        case "NFA_WATCH":
            return (
                <SvgCircleStarIcon
                    width={32}
                    height={32}
                    className="text-core-blue"
                />
            );
        case "W_SHARE_WITH":
        case "WB_SHARE_WITH":
        case "SHARE_WITH":
            return (
                <SvgCircleShareIcon
                    width={32}
                    height={32}
                    className="text-core-blue"
                />
            );
        case "GNSF":
        case "UPDATES":
        case "UPDATES_LINK":
            return (
                <SvgCircleBellWithBubbleIcon
                    width={32}
                    height={32}
                    className="text-core-blue"
                />
            );
    }
}

function getNotificationMessage(item: CreateNotification, tx: TX) {
    const link = (
        <Link to={"/my-funds"} className="text-core-blue mt-2 underline">
            <Translate>Go to my funds</Translate>
        </Link>
    );
    const youHaveSubscribedTo = (n: number) => {
        const { result } = tx(
            `You've subscribed to [[N]] fund${n > 1 ? "s" : ""}`,
        );
        return <p>{replacer(result, { n })}</p>;
    };
    const youHaveAdded = (n: number) => {
        const { result } = tx(
            `You've added [[N]] fund${n > 1 ? "s" : ""} to your watchlist`,
        );
        return <p>{replacer(result, { n })}</p>;
    };
    const fundsHaveUpdated = (n: number) => {
        const { result } = tx(
            `[[N]] fund${n > 1 ? "s" : ""} have been updated`,
        );
        const { result: result2 } = tx("Check funds with this [^] icon");
        return (
            <div>
                <p>{replacer(result, { n })}.</p>
                <p>{replacer(result2)}</p>
            </div>
        );
    };
    const emailHasShared = (email: string, n: number) => {
        const { result } = tx(
            `has shared [[N]] fund${
                n > 1 ? "s" : ""
            } with you, you can manage them below`,
        );
        return (
            <div>
                <p>
                    <b className="pii">{email}</b>
                </p>
                <p>{result}.</p>
            </div>
        );
    };
    const emailsList = (emails: string[]) => (
        <ul>
            {emails.map((email) => (
                <li key={email} className="pii">
                    {email}
                </li>
            ))}
        </ul>
    );
    const getNotified = () => {
        const { result } = tx(
            "Subscribe to funds by clicking on the [^] icons and receive email updates when fact sheets and other fund literature is published or updated.",
        );
        return replacer(result);
    };

    const wrapMessage = (children: ReactNode) => (
        <div className="text-sm">{children}</div>
    );

    switch (item.type) {
        case "WMF":
            return wrapMessage(youHaveSubscribedTo(item.count));
        case "WBMF":
        case "WB":
        case "NFS":
            return wrapMessage(
                <>
                    {youHaveSubscribedTo(item.count)}
                    {link}
                </>,
            );
        case "WMF_SHARE":
        case "WB_SHARE":
            return wrapMessage(emailHasShared(item.email, item.count));
        case "WMF_SHARE_LINK":
        case "WB_SHARE_LINK":
        case "SHARE_LINK":
            return wrapMessage(
                <>
                    {emailHasShared(item.email, item.count)}
                    {link}
                </>,
            );
        case "WMF_WATCH":
            return wrapMessage(youHaveAdded(item.count));
        case "WB_WATCH":
        case "NFA_WATCH":
            return wrapMessage(
                <>
                    {youHaveAdded(item.count)}
                    {link}
                </>,
            );
        case "W_SHARE_WITH":
        case "WB_SHARE_WITH":
        case "SHARE_WITH":
            return wrapMessage(emailsList(item.emails));
        case "UPDATES":
            return wrapMessage(fundsHaveUpdated(item.count));
        case "UPDATES_LINK":
            return wrapMessage(
                <>
                    {fundsHaveUpdated(item.count)}
                    {link}
                </>,
            );
        case "GNSF":
            return wrapMessage(getNotified());
    }
}

function getNotificationTitle(item: CreateNotification): ReactNode {
    switch (item.type) {
        case "WB":
        case "WB_SHARE":
        case "WB_SHARE_LINK":
        case "WB_WATCH":
            return <Translate>Welcome back</Translate>;
        case "WMF":
        case "WMF_SHARE":
        case "WMF_SHARE_LINK":
        case "WMF_WATCH":
            return <Translate>Welcome to my funds</Translate>;
        case "WBMF":
            // return <Translate>Welcome back to my funds</Translate>;
            return <Translate>Welcome back</Translate>;
        case "NFA_WATCH":
            return <Translate>New funds added</Translate>;
        case "WB_SHARE_WITH":
            return (
                <>
                    <Translate>Welcome back</Translate>
                    <br />
                    <Translate>You've shared funds with</Translate>:
                </>
            );
        case "W_SHARE_WITH":
            return (
                <>
                    <Translate>Welcome</Translate>
                    <br />
                    <Translate>You've shared funds with</Translate>:
                </>
            );
        case "SHARE_WITH":
            return (
                <>
                    <Translate>You've shared funds with</Translate>:
                </>
            );
        case "NFS":
            return <Translate>New fund subscriptions</Translate>;
        case "SHARE_LINK":
            return <Translate>Funds shared with you</Translate>;
        case "UPDATES":
        case "UPDATES_LINK":
            return <Translate>Fund updates</Translate>;
        case "GNSF":
            return <Translate>Get notified, subscribe to funds</Translate>;
        default:
            return "Title";
    }
}
